import React, { useState, useContext } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ShoppingCartIcon, UserIcon, MagnifyingGlassIcon, Bars3Icon, XMarkIcon } from '@heroicons/react/24/outline';
import { AuthContext } from '../../context/AuthContext';
import { CartContext } from '../../context/CartContext';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const { isAuthenticated, user, logout } = useContext(AuthContext);
  const { cartCount } = useContext(CartContext);
  const navigate = useNavigate();

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/products?search=${searchQuery}`);
      setSearchQuery('');
      setIsMenuOpen(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
    setIsMenuOpen(false);
  };

  return (
    <header className="bg-white sticky top-0 z-50">
      <div className="border-b border-gray-100">
        <nav className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex justify-between items-center h-14 lg:h-16">
            {/* Logo */}
            <Link to="/" className="text-lg font-medium tracking-wide text-gray-900 hover:text-gray-600 transition">
              STUDIO
            </Link>

            {/* Desktop Navigation - Centered */}
            <div className="hidden lg:flex items-center absolute left-1/2 transform -translate-x-1/2 space-x-8">
              <Link to="/" className="text-sm text-gray-500 hover:text-gray-900 transition">
                Home
              </Link>
              <Link to="/products" className="text-sm text-gray-500 hover:text-gray-900 transition">
                Products
              </Link>
              <Link to="/about" className="text-sm text-gray-500 hover:text-gray-900 transition">
                About
              </Link>
            </div>

            {/* Right Side */}
            <div className="flex items-center space-x-5">
              {/* Search Icon with Popup */}
              <div className="hidden md:block">
                <form onSubmit={handleSearch} className="relative">
                  <input
                    type="text"
                    placeholder="Search"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-7 pr-3 py-1.5 text-sm border-none bg-transparent focus:outline-none focus:ring-0 text-gray-700 placeholder-gray-400 w-32 focus:w-48 transition-all duration-300"
                  />
                  <MagnifyingGlassIcon className="absolute left-0 top-2 h-4 w-4 text-gray-400" />
                </form>
              </div>

              {/* Cart */}
              <Link to="/cart" className="relative group">
                <ShoppingCartIcon className="h-5 w-5 text-gray-500 group-hover:text-gray-900 transition" />
                {cartCount > 0 && (
                  <span className="absolute -top-2 -right-2 bg-gray-900 text-white text-[10px] font-medium rounded-full min-w-[16px] h-4 px-1 flex items-center justify-center">
                    {cartCount > 9 ? '9+' : cartCount}
                  </span>
                )}
              </Link>

              {/* User Menu */}
              {isAuthenticated ? (
                <div className="relative group">
                  <button className="flex items-center">
                    <div className="w-7 h-7 rounded-full flex items-center justify-center">
                      {user?.avatar ? (
                        <img src={user.avatar} alt={user.full_name} className="w-full h-full rounded-full object-cover" />
                      ) : (
                        <UserIcon className="h-4 w-4 text-gray-500 group-hover:text-gray-900 transition" />
                      )}
                    </div>
                  </button>
                  <div className="absolute right-0 mt-2 w-44 bg-white border border-gray-100 rounded-sm shadow-sm py-1 invisible group-hover:visible transition-all duration-150 z-50">
                    <Link to="/profile" className="block px-4 py-2 text-xs text-gray-600 hover:bg-gray-50">Account</Link>
                    <Link to="/orders" className="block px-4 py-2 text-xs text-gray-600 hover:bg-gray-50">Orders</Link>
                    <hr className="my-1 border-gray-100" />
                    <button onClick={handleLogout} className="block w-full text-left px-4 py-2 text-xs text-gray-400 hover:bg-gray-50">
                      Sign out
                    </button>
                  </div>
                </div>
              ) : (
                <Link to="/login" className="text-sm text-gray-500 hover:text-gray-900 transition">
                  Sign in
                </Link>
              )}

              {/* Mobile Menu Button */}
              <button onClick={() => setIsMenuOpen(!isMenuOpen)} className="lg:hidden text-gray-500">
                {isMenuOpen ? <XMarkIcon className="h-5 w-5" /> : <Bars3Icon className="h-5 w-5" />}
              </button>
            </div>
          </div>

          {/* Mobile Menu */}
          {isMenuOpen && (
            <div className="lg:hidden py-6 space-y-5 border-t border-gray-100 mt-1">
              <Link to="/" className="block text-gray-600 text-sm" onClick={() => setIsMenuOpen(false)}>
                Home
              </Link>
              <Link to="/products" className="block text-gray-600 text-sm" onClick={() => setIsMenuOpen(false)}>
                Products
              </Link>
              <Link to="/about" className="block text-gray-600 text-sm" onClick={() => setIsMenuOpen(false)}>
                About
              </Link>
              <form onSubmit={handleSearch} className="relative pt-2">
                <input
                  type="text"
                  placeholder="Search products..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-8 pr-4 py-2 text-sm border border-gray-100 bg-gray-50 focus:outline-none focus:border-gray-200"
                />
                <MagnifyingGlassIcon className="absolute left-2 top-3.5 h-4 w-4 text-gray-400" />
              </form>
              {!isAuthenticated && (
                <Link to="/login" className="block text-gray-900 text-sm font-medium pt-2" onClick={() => setIsMenuOpen(false)}>
                  Sign in
                </Link>
              )}
            </div>
          )}
        </nav>
      </div>
    </header>
  );
};

export default Header;